import { useCallback, useEffect } from "react";
import type { ConnectionSchemas } from "api-types";
import { useIntl } from "react-intl";

import { dataSourceSlugToName } from "../utils";

/**
 * Handles the oAuthError prop in complete connection and prepares error toast props
 */
export const useOAuthError = (
  completeConnection: ConnectionSchemas.CompleteConnectionPropsSchema | undefined,
  errorHandler
) => {
  const intl = useIntl();
  const errorMessages = useCallback(
    (errorCode) => {
      if (completeConnection && "dataSourceSlug" in completeConnection) {
        const errors = {
          //Facebook, GA
          access_denied: () =>
            intl.formatMessage(
              {
                id: "shared-ui: reconnection - denied access",
                defaultMessage: "{dataSourceName} denied access",
              },
              {
                dataSourceName: dataSourceSlugToName(completeConnection.dataSourceSlug),
              }
            ),
          no_accounts: () =>
            intl.formatMessage(
              {
                id: "shared-ui: reconnection - no accounts",
                defaultMessage: "{dataSourceName} has no accounts available for connection",
              },
              {
                dataSourceName: dataSourceSlugToName(completeConnection.dataSourceSlug),
              }
            ),
          account_listing_error: () =>
            intl.formatMessage(
              {
                id: "shared-ui: reconnection - account listing error",
                defaultMessage: "{dataSourceName} has accounts which can't displayed; please contact support",
              },
              {
                dataSourceName: dataSourceSlugToName(completeConnection.dataSourceSlug),
              }
            ),
          missing_permissions: () =>
            intl.formatMessage(
              {
                id: "shared-ui: reconnection - missing permissions",
                defaultMessage: "{dataSourceName} account does not have access to any data",
              },
              {
                dataSourceName: dataSourceSlugToName(completeConnection.dataSourceSlug),
              }
            ),
          invalid_facebook_configuration: () =>
            intl.formatMessage({
              id: "shared-ui: invalid_facebook_configuration",
              defaultMessage: "Unable to connect. Please contact support.",
            }),
          tiktok_lost_access_to_accounts: () =>
            intl.formatMessage({
              id: "shared-ui: tiktok_lost_access_to_accounts",
              defaultMessage: "Unable to reconnect. Lost access to some of the previously authorized accounts",
            }),
          two_step_verification_not_enrolled: () =>
            intl.formatMessage(
              {
                id: "shared-ui: reconnection - two step authentication not enrolled",
                defaultMessage: "{dataSourceName} admin requires two-step verification",
              },
              {
                dataSourceName: dataSourceSlugToName(completeConnection.dataSourceSlug),
              }
            ),
          shop_uniqueness_violation: () =>
            intl.formatMessage({
              id: "shared-ui: shop uniqueness violation exception",
              defaultMessage: "This connection already exists. Please connect a different store.",
            }),
          amazon_shop_uniqueness_violation: () =>
            intl.formatMessage({
              id: "shared-ui: amazon shop uniqueness violation exception",
              defaultMessage:
                "This connection already exists. Please connect a different store. Note: Amazon stores are identified on a region level (North America, Europe, etc.)",
            }),
          shop_reconnection_difference: () =>
            intl.formatMessage({
              id: "shared-ui: shop reconnection difference exception",
              defaultMessage: "Reconnecting with a different store is not allowed. Please connect the original store.",
            }),
          unsupported_currency: () =>
            intl.formatMessage({
              id: "shared-ui:  shopify connection error currency",
              defaultMessage: "We do not currently support your store currency.",
            }),
          tiktok_insufficient_scope: () =>
            intl.formatMessage({
              id: "shared-ui: tiktok insufficient scope",
              defaultMessage: "Insufficient permissions. Please reconnect selecting all of the requested permissions.",
            }),
          tiktok_reconnect_error: () =>
            intl.formatMessage(
              {
                id: "shared-ui: unable to complete tiktok reconnection",
                defaultMessage: "Unable to complete {dataSourceName} reconnection",
              },
              {
                dataSourceName: dataSourceSlugToName(completeConnection.dataSourceSlug),
              }
            ),
          bigcommerce_single_click_exception: () =>
            intl.formatMessage({
              id: "shared-ui: unable to complete bigcommerce singe-click connection",
              defaultMessage:
                "Error while trying to establish connection. Please try to re-install the application from the BigCommerce dashboard.",
            }),
          // This case covers stripe, woocommerce, magento and other
          default: () =>
            intl.formatMessage(
              {
                id: "shared-ui: unable to complete connection",
                defaultMessage: "Unable to complete {dataSourceName} connection",
              },
              {
                dataSourceName: dataSourceSlugToName(completeConnection.dataSourceSlug),
              }
            ),
        };
        return errors[errorCode] ? errors[errorCode]() : errors.default();
      }
    },
    [intl, completeConnection]
  );

  useEffect(() => {
    if (completeConnection && completeConnection["oAuthError"]) {
      errorHandler(errorMessages(completeConnection.oAuthError));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intl, completeConnection, errorMessages]);
};
